import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, BarChart3, Monitor, Smartphone, Tablet } from "lucide-react";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, Tooltip, Legend);

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV !== "production" ? "http://localhost:8000" : "");
const API = `${BACKEND_URL}/api`;

const DeviceIcon = ({ type }) => {
  if (type === "Mobile") return <Smartphone className="h-4 w-4" />;
  if (type === "Tablet") return <Tablet className="h-4 w-4" />;
  return <Monitor className="h-4 w-4" />;
};

function AnalyticsOverview() {
  const navigate = useNavigate();
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/qr/list`, { credentials: "include" });
        if (res.ok) setQrCodes(await res.json());
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen bg-zinc-50">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-[#0047FF]" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50" data-testid="analytics-overview-page">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 lg:p-8">
          <div className="mb-8 pl-12 lg:pl-0">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Analytics
            </h1>
            <p className="mt-1 text-sm text-zinc-500">Select a QR code to view detailed analytics</p>
          </div>

          {qrCodes.length === 0 ? (
            <Card className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardContent className="flex flex-col items-center py-16">
                <BarChart3 className="h-12 w-12 text-zinc-200 mb-4" strokeWidth={1} />
                <p className="text-sm font-bold text-zinc-500 mb-1">No analytics data</p>
                <p className="text-xs text-zinc-400">Create QR codes to start tracking scans</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {qrCodes.map((qr, i) => (
                <motion.div
                  key={qr.qr_id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                >
                  <Card
                    className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 cursor-pointer group"
                    onClick={() => navigate(`/analytics/${qr.qr_id}`)}
                    data-testid={`analytics-card-${qr.short_code}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline" className="text-xs font-bold uppercase">{qr.qr_type}</Badge>
                        <span className="text-2xl font-black text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                          {qr.scan_count}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-zinc-950 truncate">{qr.label}</p>
                      <p className="text-xs text-zinc-400 mt-1 truncate">{qr.short_code}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function AnalyticsDetail() {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API}/qr/${qrId}/analytics`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [qrId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex h-screen bg-zinc-50">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-[#0047FF]" />
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-screen bg-zinc-50">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-sm text-zinc-500">QR code not found</p>
        </main>
      </div>
    );
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#09090B",
        titleColor: "#FFFFFF",
        bodyColor: "#FFFFFF",
        cornerRadius: 8,
        padding: 12,
        titleFont: { size: 12, weight: "bold" },
        bodyFont: { size: 11 },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11, family: "'Satoshi'" } } },
      y: { grid: { display: false }, ticks: { font: { size: 11, family: "'Satoshi'" } } },
    },
  };

  const dailyLabels = Object.keys(data.daily_counts).sort();
  const lineData = {
    labels: dailyLabels.map((d) => new Date(d).toLocaleDateString("en", { month: "short", day: "numeric" })),
    datasets: [{
      label: "Scans",
      data: dailyLabels.map((d) => data.daily_counts[d]),
      borderColor: "#0047FF",
      backgroundColor: "rgba(0,71,255,0.08)",
      fill: true,
      tension: 0.4,
      pointBackgroundColor: "#0047FF",
      pointRadius: 4,
      borderWidth: 2,
    }],
  };

  const deviceLabels = Object.keys(data.device_counts);
  const doughnutData = {
    labels: deviceLabels,
    datasets: [{
      data: deviceLabels.map((d) => data.device_counts[d]),
      backgroundColor: ["#0047FF", "#09090B", "#A1A1AA", "#E4E4E7"],
      borderWidth: 0,
    }],
  };

  const browserLabels = Object.keys(data.browser_counts);
  const barData = {
    labels: browserLabels,
    datasets: [{
      label: "Scans",
      data: browserLabels.map((b) => data.browser_counts[b]),
      backgroundColor: "#0047FF",
      borderRadius: 6,
      barThickness: 32,
    }],
  };

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50" data-testid="analytics-detail-page">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 lg:p-8">
          <div className="mb-8 pl-12 lg:pl-0">
            <button
              onClick={() => navigate("/analytics")}
              className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-950 transition-colors mb-4"
              data-testid="back-to-analytics"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Analytics
            </button>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {data.qr.label}
              </h1>
              <Badge variant="outline" className="text-xs font-bold uppercase">{data.qr.qr_type}</Badge>
            </div>
            <p className="mt-1 text-sm text-zinc-500">
              {data.total_scans} total scans | Code: <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-xs">{data.qr.short_code}</code>
            </p>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Line Chart */}
            <Card className="lg:col-span-2 border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-sm font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Scans Over Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]" data-testid="scans-line-chart">
                  {dailyLabels.length > 0 ? (
                    <Line data={lineData} options={chartOptions} />
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-zinc-400">No scan data yet</div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Doughnut Chart */}
            <Card className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-sm font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Devices
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px] flex items-center justify-center" data-testid="device-doughnut-chart">
                  {deviceLabels.length > 0 ? (
                    <Doughnut
                      data={doughnutData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: "70%",
                        plugins: {
                          legend: { position: "bottom", labels: { font: { size: 11, family: "'Satoshi'" }, padding: 16, usePointStyle: true, pointStyle: "circle" } },
                          tooltip: chartOptions.plugins.tooltip,
                        },
                      }}
                    />
                  ) : (
                    <p className="text-sm text-zinc-400">No data</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Browser Bar Chart */}
          <Card className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] mb-8">
            <CardHeader>
              <CardTitle className="text-sm font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Browsers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[220px]" data-testid="browser-bar-chart">
                {browserLabels.length > 0 ? (
                  <Bar data={barData} options={chartOptions} />
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-zinc-400">No data</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Scans Table */}
          <Card className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-sm font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Recent Scans
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {data.scans.length === 0 ? (
                <div className="py-12 text-center text-sm text-zinc-400">No scans recorded yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-200 bg-zinc-50">
                      <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4">Device</TableHead>
                      <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4">Browser</TableHead>
                      <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4">OS</TableHead>
                      <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4">IP</TableHead>
                      <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4">Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.scans.map((s) => (
                      <TableRow key={s.scan_id} className="border-zinc-100 hover:bg-zinc-50 transition-colors">
                        <TableCell className="py-3 px-4">
                          <div className="flex items-center gap-2 text-sm text-zinc-950">
                            <DeviceIcon type={s.device} />
                            {s.device}
                          </div>
                        </TableCell>
                        <TableCell className="py-3 px-4 text-sm text-zinc-600">{s.browser}</TableCell>
                        <TableCell className="py-3 px-4 text-sm text-zinc-600">{s.os_name}</TableCell>
                        <TableCell className="py-3 px-4">
                          <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono text-zinc-500">{s.ip}</code>
                        </TableCell>
                        <TableCell className="py-3 px-4 text-sm text-zinc-500">
                          {new Date(s.scanned_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

export default function Analytics() {
  const { qrId } = useParams();
  return qrId ? <AnalyticsDetail /> : <AnalyticsOverview />;
}
